import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { Navigation, ShieldCheck, Truck, Zap } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const PORT_IMG =
  "https://images.unsplash.com/photo-1590497008432-598f04441de8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHBvcnQlMjBjb250YWluZXIlMjB0ZXJtaW5hbCUyMG5pZ2h0JTIwZGFya3xlbnwwfHx8fDE3ODk2MjQxMDF8MA&ixlib=rb-4.1.0&q=85";
const HIGHWAY_IMG =
  "https://images.unsplash.com/photo-1581010105267-67447703cfe9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwbmlnaHQlMjBzcGVlZCUyMGxpZ2h0c3xlbnwwfHx8fDE3ODk2MjQxMDF8MA&ixlib=rb-4.1.0&q=85";

const SERVICES = [
  {
    id: "load-matching",
    icon: Truck,
    title: "FTL & LTL LOAD MATCHING",
    tag: "FTL · PARTIAL · LTL",
    body: "Commercial shipments coordinated across dry van, refrigerated, flatbed, specialized, and intermodal equipment needs.",
    img: PORT_IMG,
    big: true,
  },
  {
    id: "carrier-network",
    icon: ShieldCheck,
    title: "CARRIER QUALIFICATION",
    tag: "AUTHORITY · INSURANCE · SAFETY",
    body: "Carrier operating authority, insurance, safety information, equipment, and communication practices are reviewed during selection.",
  },
  {
    id: "rate-engine",
    icon: Zap,
    title: "RATE COORDINATION",
    tag: "SHIPMENT-SPECIFIC PRICING",
    body: "Pricing is prepared around the shipment details, lane, equipment requirements, timing, and available capacity.",
  },
  {
    id: "tracking",
    icon: Navigation,
    title: "SHIPMENT COMMUNICATION",
    tag: "PICKUP → DELIVERY",
    body: "Direct communication supports pickup coordination, in-transit updates, delivery status, and exception handling.",
    img: HIGHWAY_IMG,
    big: true,
  },
];

const TiltCard = ({ service, index }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), {
    stiffness: 140,
    damping: 20,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-4, 4]), {
    stiffness: 140,
    damping: 20,
  });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const Icon = service.icon;

  return (
    <motion.div
      ref={ref}
      data-testid={`service-card-${service.id}`}
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      style={{ rotateX, rotateY, transformPerspective: 1000 }}
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: 0.08 * index, ease: EASE }}
      className={`group relative overflow-hidden border border-[#222736] bg-[#12141C] transition-colors duration-500 hover:border-[#FF5500]/60 ${
        service.big ? "md:col-span-2" : ""
      }`}
    >
      {service.img && (
        <>
          <img
            src={service.img}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-25 transition-opacity duration-700 group-hover:opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#12141C] via-[#12141C]/70 to-[#12141C]/30" />
        </>
      )}
      <div className="relative flex h-full min-h-[280px] flex-col justify-between p-8">
        <div className="flex items-start justify-between">
          <div className="flex h-12 w-12 items-center justify-center border border-[#FF5500]/40 bg-[#FF5500]/10">
            <Icon className="h-5 w-5 text-[#FF5500]" />
          </div>
          <span className="font-mono text-[10px] font-bold tracking-[0.2em] text-[#F59E0B]">
            {service.tag}
          </span>
        </div>
        <div className="pt-10">
          <h3 className="font-display text-xl font-bold text-white sm:text-2xl">
            {service.title}
          </h3>
          <p className="mt-3 max-w-md text-base leading-7 text-slate-300 lg:text-[17px]">
            {service.body}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default function Services() {
  return (
    <section id="services" className="relative scroll-mt-20 py-28">
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#F59E0B]">
              02 // WHAT WE DO
            </p>
            <h2 className="mt-4 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
              Core Capabilities
            </h2>
          </div>
          <p className="max-w-sm text-base leading-7 text-slate-400">
            Four practical services that support freight planning, carrier
            selection, pricing, and shipment communication.
          </p>
        </motion.div>

        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {SERVICES.map((s, i) => (
            <TiltCard key={s.id} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
